import { useDataGrid, List } from '@refinedev/mui'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Box, Chip, IconButton, Link, MenuItem, Stack, TextField, Typography } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useLocation, useNavigate } from 'react-router-dom'
import { useState } from 'react'

const ACTION_OPTIONS = ['', 'create', 'update', 'no_action']

const MemoryActivityPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedRow, setSelectedRow] = useState<any>(null)

  const params = new URLSearchParams(location.search)
  const orgName = params.get('org_name') || ''
  const needName = params.get('need_name') || ''
  const opportunityName = params.get('opportunity_name') || ''
  const action = params.get('action') || ''
  const dateFrom = params.get('date_from') || ''
  const dateTo = params.get('date_to') || ''

  const filters = [] as any[]
  if (orgName) filters.push({ field: 'org_name', operator: 'contains', value: orgName })
  if (needName) filters.push({ field: 'need_name', operator: 'contains', value: needName })
  if (opportunityName) filters.push({ field: 'opportunity_name', operator: 'contains', value: opportunityName })
  if (action) filters.push({ field: 'action', operator: 'eq', value: action })
  if (dateFrom) filters.push({ field: 'created_at', operator: 'gte', value: dateFrom })
  if (dateTo) filters.push({ field: 'created_at', operator: 'lte', value: `${dateTo} 23:59:59` })

  const { dataGridProps } = useDataGrid({
    resource: 'memory-activity',
    filters: {
      permanent: filters,
    },
    sorters: {
      initial: [{ field: 'created_at', order: 'desc' }],
    },
  })

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(location.search)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    navigate({ pathname: location.pathname, search: next.toString() })
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', minWidth: 80 },
    {
      field: 'org_name',
      headerName: 'Org',
      minWidth: 220,
      flex: 1,
      valueGetter: (params) => params.row.org_name || params.row.org_uuid,
    },
    {
      field: 'action',
      headerName: 'Action',
      minWidth: 120,
      renderCell: ({ value }) => {
        const color = value === 'create' ? 'success' : value === 'update' ? 'warning' : 'default'
        return <Chip size="small" color={color} label={value} />
      },
    },
    {
      field: 'need_name',
      headerName: 'Need',
      minWidth: 180,
      valueGetter: (params) => params.row.need_name || `#${params.row.need_id || 'N/A'}`,
    },
    {
      field: 'opportunity_name',
      headerName: 'Opportunity',
      minWidth: 220,
      valueGetter: (params) => params.row.opportunity_name || params.row.opportunity_uuid || 'N/A',
    },
    {
      field: 'memory_content',
      headerName: 'Memory',
      minWidth: 250,
      flex: 2,
      renderCell: ({ row }) => {
        if (!row.memory_content) return <Typography variant="body2" color="text.secondary">—</Typography>
        return (
          <Link href={`/memories/${row.memory_point_id}`} underline="hover" sx={{ display: 'block', maxWidth: 400 }}>
            <Typography variant="body2" noWrap>{row.memory_content}</Typography>
          </Link>
        )
      },
    },
    {
      field: 'reasoning',
      headerName: 'Reasoning',
      minWidth: 220,
      flex: 1,
      renderCell: ({ value }) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 440 }}>
          {value || ''}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      minWidth: 180,
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : ''),
    },
    {
      field: 'actions',
      headerName: 'Details',
      minWidth: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/memories/activity/${row.id}`) }}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <List title="Memory Activity">
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <TextField label="Org Name" size="small" value={orgName} onChange={(e) => setParam('org_name', e.target.value)} />
        <TextField label="Need Name" size="small" value={needName} onChange={(e) => setParam('need_name', e.target.value)} />
        <TextField
          label="Opportunity Name"
          size="small"
          value={opportunityName}
          onChange={(e) => setParam('opportunity_name', e.target.value)}
        />
        <TextField select label="Action" size="small" value={action} onChange={(e) => setParam('action', e.target.value)}>
          {ACTION_OPTIONS.map((option) => (
            <MenuItem key={option || 'all'} value={option}>
              {option || 'All'}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Date From"
          size="small"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setParam('date_from', e.target.value)}
        />
        <TextField
          label="Date To"
          size="small"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setParam('date_to', e.target.value)}
        />
      </Stack>

      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        getRowId={(row) => row.id}
        onRowClick={(params) => setSelectedRow(params.row)}
      />

      {selectedRow ? (
        <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
          <Typography variant="h6">Activity #{selectedRow.id}</Typography>
          {selectedRow.memory_content && (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Memory {selectedRow.action === 'create' ? 'Created' : selectedRow.action === 'update' ? 'Updated' : 'Affected'}
                {selectedRow.memory_status && <Chip size="small" label={selectedRow.memory_status} sx={{ ml: 1 }} />}
                {selectedRow.memory_category && <Chip size="small" label={selectedRow.memory_category} variant="outlined" sx={{ ml: 0.5 }} />}
                {selectedRow.memory_scope && <Chip size="small" label={selectedRow.memory_scope} variant="outlined" sx={{ ml: 0.5 }} />}
              </Typography>
              <Typography variant="body2">{selectedRow.memory_content}</Typography>
              {selectedRow.memory_point_id && (
                <Link href={`/memories/${selectedRow.memory_point_id}`} variant="body2" sx={{ mt: 0.5, display: 'inline-block' }}>
                  View memory →
                </Link>
              )}
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Reasoning:</strong> {selectedRow.reasoning || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Original Answer:</strong> {selectedRow.original_answer || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Edited Answer:</strong> {selectedRow.edited_answer || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Memories Used:</strong>{' '}
            {Array.isArray(selectedRow.memories_used) && selectedRow.memories_used.length
              ? selectedRow.memories_used.join(', ')
              : 'None'}
          </Typography>
        </Box>
      ) : null}
    </List>
  )
}

export default MemoryActivityPage
